import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { buildTourSteps, resolveTourRole, isSupporterOnly, surfacePath } from '../../tours/tourSteps';
import { canScanCards } from '../../lib/membershipAccess';
import './GuidedTour.css';

/**
 * GuidedTour — lightweight spotlight tour engine (slice 2, no library).
 *
 * Mounted on a page ("surface"): ClubHub for everyone except sponsors, whose
 * home is SponsorScanPage; and — since W-2 — AdminPage too, so the admin tour
 * can walk Club Admin tab by tab. Auto-starts on first visit to a club where
 * club_members.tour_completed_at is null; Close and Finish both stamp it
 * (POST /clubs/:slug/me/tour-complete) so nobody is nagged. Relaunch comes
 * from the navbar account menu ("Take the tour") via a custom event, or a
 * sessionStorage flag when the menu had to navigate here first.
 *
 * Steps with a `target` selector get a spotlight cutout over the live UI
 * element (box-shadow trick) with the card beside it; on small screens the
 * card docks as a bottom sheet. Steps without a resolvable target fall back
 * to `fallback`, then to skipping the step entirely — so a tour never
 * strands on an element the current viewport doesn't render.
 *
 * Mid-tour navigation (W-2): a step may carry `surface` ('hub' | 'admin' |
 * 'scan' | 'wallet' | 'attribution' | 'account' — see surfacePath) and `tab`.
 * Every tour walks onto the page it is talking about (Stuart, 16 Sept): the
 * engine is mounted on each of those pages (never auto-starting off the
 * role's home surface) so a hop can resume there. Moving onto a step whose surface differs from the one
 * this engine is mounted on writes { clubSlug, stepIndex } to sessionStorage
 * and navigates there; the destination's GuidedTour resumes from that index
 * once the page has settled. A `tab` on the same surface just rewrites the
 * URL (`?tab=…`, which AdminPage honours) and waits for the target to
 * appear. `openMenu` steps pop the ☰ menu first on phones so the spotlight
 * lands on the real link.
 */

const MOBILE_BREAKPOINT = 640;
const SPOT_PADDING = 6;
const RESUME_KEY = 'marakin_tour_resume';

// First VISIBLE match — the same data-tour anchor can exist twice (desktop
// nav link hidden on phones + the mobile-menu link), and querySelector alone
// would return the hidden one.
const findVisible = (selector) => {
  if (!selector) return null;
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    if (el.getClientRects().length) return el; // display:none has no rects
  }
  return null;
};

// A hop that never lands (back button mid-hop, a redirect, an expired
// session) must not park a key that suppresses auto-start for the rest of
// the tab session — anything older than a minute is stale and discarded.
const RESUME_TTL_MS = 60 * 1000;
const readResume = () => {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    const r = raw ? JSON.parse(raw) : null;
    if (r && (!r.at || Date.now() - r.at > RESUME_TTL_MS)) { sessionStorage.removeItem(RESUME_KEY); return null; }
    return r;
  } catch { return null; }
};
const writeResume = (v) => {
  try {
    if (v) sessionStorage.setItem(RESUME_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(RESUME_KEY);
  } catch { /* storage unavailable */ }
};

const GuidedTour = ({ club, surface = 'hub' }) => {
  const { clubSlug } = useParams();
  const navigate = useNavigate();
  const { user, getMembership, refreshUser } = useAuth();
  const membership = clubSlug ? getMembership(clubSlug) : null;

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [spot, setSpot] = useState(null); // { top, left, width, height } viewport coords
  const autoStarted = useRef(false);
  const openRef = useRef(false);
  const leavingRef = useRef(false); // set when WE navigate away mid-tour

  const role = resolveTourRole(membership);
  // Which surface this membership's tour belongs on — sponsors live on /scan.
  const homeSurface = role === 'sponsor' ? 'scan' : 'hub';
  // WT-9: supporters can't answer Going / Not going on team events.
  const supporterOnly = isSupporterOnly(membership);
  // WT-24: club staff get the Wallet → Scan cards step (same rule as the
  // Wallet's own Scan cards button).
  const canScan = canScanCards(membership);

  const steps = useMemo(() => {
    if (!role) return [];
    return buildTourSteps({
      role,
      clubName: club?.name,
      clubSlug,
      isParent: membership?.role === 'parent' || membership?.isGuardian,
      isSupporter: supporterOnly,
      verified: !!club?.verified_at,
      submitted: !!club?.verification_submitted_at,
      // v35 membership cards: admin copy mentions the toggles only when the
      // club runs cards; the member Wallet step only when they hold one.
      cardsOn: !!club?.membership_cards_enabled,
      holdsCard: !!membership?.holdsCard,
      // B-45: the member tour's Shop step exists only when Kit Swap is on.
      kitSwapOn: !!club?.kit_swap_on,
      canScan,
    });
  }, [role, club?.name, club?.verified_at, club?.verification_submitted_at, club?.membership_cards_enabled, club?.kit_swap_on, clubSlug, membership?.role, membership?.isGuardian, membership?.holdsCard, supporterOnly, canScan]);

  const stepSurface = useCallback((s) => (s && s.surface) || homeSurface, [homeSurface]);

  // A start asked for before the steps exist (club data still loading after a
  // "Take the tour" hop from another page) is REMEMBERED and fired the moment
  // the steps arrive — it used to be dropped, which is why the menu item
  // needed two taps (Stuart, 16 Sept).
  const pendingStart = useRef(false);
  const [pendingTick, setPendingTick] = useState(0); // bumps so the effect below re-checks the ref
  const start = useCallback(() => {
    if (!steps.length) { pendingStart.current = true; setPendingTick(t => t + 1); return; }
    pendingStart.current = false;
    writeResume(null);
    setStepIndex(0);
    setOpen(true);
  }, [steps.length]);
  // Fires a pending start once the steps exist. The relaunch flag used to
  // schedule a plain setTimeout inside an effect whose deps changed as the
  // club data landed — React's cleanup cancelled the timer and, the flag
  // already consumed, nothing rescheduled it. This effect re-arms itself on
  // every re-run while the request is still pending, so it can't be lost.
  useEffect(() => {
    if (!pendingStart.current || !steps.length) return undefined;
    const t = setTimeout(() => { if (pendingStart.current) start(); }, 500);
    return () => clearTimeout(t);
  }, [steps.length, start, pendingTick]);

  // ── Auto-start on first visit (tour never completed for this club) ──
  useEffect(() => {
    if (autoStarted.current || !user || !membership || membership.implicit) return;
    if (surface !== homeSurface) return;
    if (membership.tourCompletedAt) return;
    if (readResume()?.clubSlug === clubSlug) return; // a resume is in flight — don't restart
    // Let the page settle, and NEVER start underneath an open modal — the
    // consent sheet (z-index 1400) sits above the tour (1300), so a brand-new
    // member would get the tour running behind it. Poll until clear.
    // W-20: the guard flag is set when the tour STARTS, not when the poll is
    // armed — answering the consent sheet refreshes the user, which re-runs
    // this effect and its cleanup killed the pending poll; with the flag
    // already set, the re-run bailed and the tour never fired (Stuart's
    // repro, 14 Sept). Now the re-run simply re-arms the poll.
    const t = setInterval(() => {
      if (document.querySelector('.modal-overlay')) return;
      clearInterval(t);
      autoStarted.current = true;
      start();
    }, 900);
    return () => clearInterval(t);
  }, [user, membership, surface, homeSurface, start, clubSlug]);

  // ── Relaunch: navbar event + navigation flag ──
  useEffect(() => {
    const onEvent = () => start();
    window.addEventListener('marakin:start-tour', onEvent);
    // Only the home surface consumes the relaunch flag — a tour engine on
    // another page must leave it for the hub to find.
    let flagged = false;
    if (surface === homeSurface) {
      try {
        flagged = sessionStorage.getItem('marakin_tour_relaunch') === clubSlug;
        if (flagged) sessionStorage.removeItem('marakin_tour_relaunch');
      } catch { /* storage unavailable */ }
    }
    if (flagged) {
      // Mark the request; the pending-start effect runs it once the steps
      // exist (never a bare timer here — see the note above start()).
      pendingStart.current = true;
      setPendingTick(t => t + 1);
    }
    return () => window.removeEventListener('marakin:start-tour', onEvent);
  }, [clubSlug, surface, homeSurface, start]);

  // ── Resume after a mid-tour navigation (W-2) ──
  // The key is consumed only when the tour actually opens: this effect
  // re-runs as the page's data lands (steps depend on the club), and a
  // cleared-then-cancelled timer would silently drop the resume.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || !steps.length || !membership) return;
    const r = readResume();
    if (!r || r.clubSlug !== clubSlug) return;
    const idx = Number(r.stepIndex);
    if (!Number.isInteger(idx) || idx < 0 || idx >= steps.length) { writeResume(null); return; }
    if (stepSurface(steps[idx]) !== surface) return; // meant for another page
    const t = setTimeout(() => {
      resumed.current = true;
      autoStarted.current = true; // a resumed tour must never be restarted from 0 by the auto-start poll
      writeResume(null);
      setStepIndex(idx);
      setOpen(true);
    }, 500);
    return () => clearTimeout(t);
  }, [steps, membership, clubSlug, surface, stepSurface]);

  // ── Finish / dismiss both stamp completion (no nagging) ──
  const finish = useCallback((completed) => {
    setOpen(false);
    writeResume(null);
    if (!membership || membership.implicit) return;
    if (!membership.tourCompletedAt) {
      axios.post(`/clubs/${clubSlug}/me/tour-complete`)
        .then(() => refreshUser())
        .catch(() => {});
    }
  // eslint-disable-next-line
  }, [clubSlug, membership]);

  // Leaving the page (not via the tour's own navigation) while the tour is
  // open counts as dismissing it — otherwise the hub would restart the tour
  // on every visit for someone who wandered off halfway.
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  // Always the LATEST finish: the [] effect below would otherwise hold the
  // mount-time closure, whose `membership` is often still null on a cold load
  // (/auth/me lands after the club query), so the stamp silently no-op'd.
  const finishRef = useRef(finish);
  finishRef.current = finish;
  useEffect(() => () => {
    if (openRef.current && !leavingRef.current) finishRef.current(false);
  }, []);

  // ── Step navigation ──
  const step = steps[stepIndex];

  const hopTo = useCallback((idx) => {
    const s = steps[idx];
    const dest = stepSurface(s);
    const path = surfacePath(dest, clubSlug);
    const search = s.tab ? `?tab=${encodeURIComponent(s.tab)}` : '';
    if (dest !== surface) {
      // Different page: park the index, go there, let that page's engine resume.
      writeResume({ clubSlug, stepIndex: idx, at: Date.now() });
      leavingRef.current = true;
      setOpen(false);
      navigate(`${path}${search}`);
      return true;
    }
    if (s.tab) navigate(`${path}${search}`, { replace: true });
    return false;
  }, [steps, stepSurface, surface, clubSlug, navigate]);

  // Advance over steps whose target can't be found (direction-aware). Steps
  // that carry navigation are always accepted — their target only exists
  // after the hop, so it's the position effect that waits for it.
  const goTo = useCallback((idx, dir) => {
    let i = idx;
    while (i >= 0 && i < steps.length) {
      const s = steps[i];
      const navigational = !!s.tab || stepSurface(s) !== surface;
      if (navigational || !s.target || findVisible(s.target) || findVisible(s.fallback)) {
        if (!hopTo(i)) setStepIndex(i);
        return;
      }
      i += dir;
    }
    // Ran off the end skipping — treat as finish.
    if (i >= steps.length) finish(true);
    else setStepIndex(0);
    // eslint-disable-next-line
  }, [steps, surface, stepSurface, hopTo]);

  // ── Resolve current step's element ──
  const resolveStep = useCallback((idx) => {
    const s = steps[idx];
    if (!s) return { el: null, centered: true };
    if (!s.target) return { el: null, centered: true };
    const el = findVisible(s.target) || findVisible(s.fallback);
    return { el, centered: false };
  }, [steps]);

  // ── Position the spotlight over the live element ──
  useEffect(() => {
    if (!open) return;
    let raf = null;
    let tries = 0;
    let poll = null;
    const position = () => {
      const { el, centered } = resolveStep(stepIndex);
      if (centered || !el) { setSpot(null); return false; }
      const r = el.getBoundingClientRect();
      setSpot({
        top: r.top - SPOT_PADDING,
        left: r.left - SPOT_PADDING,
        width: r.width + SPOT_PADDING * 2,
        height: r.height + SPOT_PADDING * 2,
      });
      return true;
    };
    // "Arrived" only when the PRIMARY target is up (or the step has no
    // target); a fallback-only resolution positions the card but keeps
    // polling — e.g. the verification form renders after its GET, and until
    // then the spotlight would sit on the Sponsors tab button for good.
    const s = steps[stepIndex];
    const arrive = () => {
      const { el } = resolveStep(stepIndex);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        position();
        return !s?.target || !!findVisible(s.target);
      }
      return false;
    };
    // Targets behind the ☰ menu (phones) or on a tab that has just been
    // switched to render a beat after the step is entered — open the menu
    // if asked, then poll briefly before settling for a centered card.
    if (s?.target && !findVisible(s.target) && s.openMenu) {
      window.dispatchEvent(new CustomEvent('marakin:open-menu'));
    }
    if (!arrive()) {
      if (!resolveStep(stepIndex).el) setSpot(null);
      poll = setInterval(() => {
        tries += 1;
        if (arrive() || tries > 12) clearInterval(poll);
      }, 200);
    }
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(position); };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    const settle = setTimeout(position, 350); // after smooth scroll
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearInterval(poll);
    };
  }, [open, stepIndex, resolveStep, steps]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') finish(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, finish]);

  // B-51: the card's real height (copy length varies ~150–390px), measured
  // after each render, so desktop placement never pushes Next off screen.
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(220);
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight;
    if (h && Math.abs(h - cardH) > 2) setCardH(h);
  });

  if (!open || !step) return null;

  const isLast = stepIndex === steps.length - 1;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;

  // Card placement: under the spotlight if it fits, else above; centered
  // when there's no target; bottom sheet on mobile (CSS handles the dock).
  // B-51: when it fits neither side (a tall target, e.g. the Wallet's club
  // card), dock it to the bottom of the screen over the target rather than
  // above the top edge where its ✕ and Next can't be reached.
  let cardStyle = {};
  if (!isMobile && spot) {
    const cardW = Math.min(380, window.innerWidth - 32);
    const below = spot.top + spot.height + 14;
    const fitsBelow = below + cardH + 16 <= window.innerHeight;
    const fitsAbove = spot.top - 14 - cardH >= 16;
    cardStyle = {
      position: 'fixed',
      width: cardW,
      left: Math.max(16, Math.min(spot.left, window.innerWidth - cardW - 16)),
      ...(fitsBelow ? { top: Math.max(16, below) }
        : fitsAbove ? { bottom: window.innerHeight - spot.top + 14 }
        : { bottom: 16 }),
    };
  }

  // B-51: what this step is pointing at, for tools/tour-check (read-only
  // markers; nothing in the app reads them). target = the step's own element,
  // fallback = its stand-in, centered = a no-target step, missing = it has a
  // target but neither element is on the page (a broken step).
  const targetState = !step.target ? 'centered'
    : findVisible(step.target) ? 'target'
    : findVisible(step.fallback) ? 'fallback' : 'missing';

  return (
    <div className="gt-root" role="dialog" aria-modal="true" aria-label="Guided tour"
      data-step={stepIndex} data-steps={steps.length} data-target-state={targetState}>
      {/* Spotlight (targeted) or plain scrim (centered) */}
      {spot ? (
        <div
          className="gt-spotlight"
          style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
        />
      ) : (
        <div className="gt-scrim" />
      )}

      {/* B-51 safety net: a way out that can't go missing with the card
          (Stuart was stuck on Club → Settings, 8 Oct; only Escape worked). */}
      <button className="gt-exit" onClick={() => finish(false)}>
        <X size={14} /> Exit tour
      </button>

      <div ref={cardRef} className={`gt-card ${!spot ? 'gt-card-centered' : ''} ${isMobile ? 'gt-card-sheet' : ''}`} style={cardStyle}>
        <button className="gt-close" aria-label="Close tour" onClick={() => finish(false)}>
          <X size={16} />
        </button>
        <h3 className="gt-title">{step.title}</h3>
        <p className="gt-body">{step.body}</p>
        <div className="gt-footer">
          <div className="gt-dots" aria-label={`Step ${stepIndex + 1} of ${steps.length}`}>
            {steps.map((_, i) => (
              <span key={i} className={`gt-dot ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`} />
            ))}
          </div>
          <div className="gt-buttons">
            {stepIndex > 0 && (
              <button className="gt-btn gt-btn-ghost" onClick={() => goTo(stepIndex - 1, -1)}>
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {isLast ? (
              <button className="gt-btn gt-btn-primary" onClick={() => finish(true)}>
                <Check size={14} /> Done
              </button>
            ) : (
              <button className="gt-btn gt-btn-primary" onClick={() => goTo(stepIndex + 1, +1)}>
                {step.nextLabel || 'Next'} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
